/**
 * DashboardSummary — Summary stats cards for the creator dashboard.
 *
 * Displays: Tokens Created, Total 24h Volume, Total Accumulated Fees.
 * Computes totals client-side from the tokens array.
 * Accumulated fees = availableAmount + totalClaimedAmount (lifetime, native currency).
 *
 * Story 6.2 — Task 3
 */

import type { CreatorTokenWithStats } from "@repo/shared-types";

import {
	formatCompact,
	formatCount,
	formatNativeCurrency,
	formatPriceChange,
} from "../../lib/format-number";
import { getNativeCurrencySymbol } from "../../lib/wagmi-config";

type DashboardSummaryProps = {
	tokens: CreatorTokenWithStats[];
	isLoading: boolean;
};

type SummaryCardProps = {
	label: string;
	value: string;
	subtext?: string;
	trend?: { text: string; className: string } | null;
};

/**
 * Sum lifetime accumulated fees across all tokens.
 * Lifetime revenue = availableAmount + totalClaimedAmount per token (both in wei).
 */
function computeTotalAccumulatedFees(tokens: CreatorTokenWithStats[]): string {
	let totalWei = 0n;

	for (const token of tokens) {
		if (token.accumulatedFees) {
			const available = BigInt(token.accumulatedFees.availableAmount);
			const claimed = BigInt(token.accumulatedFees.totalClaimedAmount);
			totalWei += available + claimed;
		}
	}

	return formatNativeCurrency(totalWei, getNativeCurrencySymbol());
}

function SummaryCard({ label, value, subtext, trend }: SummaryCardProps) {
	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5">
			<p className="text-xs font-medium text-pastel-gray uppercase tracking-wide">{label}</p>
			<div className="mt-1 flex items-baseline gap-2">
				<p className="text-2xl font-bold text-pastel-black">{value}</p>
				{trend && trend.text !== "--" && (
					<span className={`text-xs font-medium ${trend.className}`}>{trend.text}</span>
				)}
			</div>
			{subtext && <p className="mt-0.5 text-xs text-pastel-gray">{subtext}</p>}
		</div>
	);
}

function SummarySkeleton() {
	return (
		<div className="grid grid-cols-1 sm:grid-cols-3 gap-4 animate-pulse">
			{Array.from({ length: 3 }).map((_, i) => (
				<div
					key={`summary-skeleton-${String(i)}`}
					className="rounded-xl border border-pastel-border bg-white p-5"
				>
					<div className="h-3 w-20 bg-pastel-light-gray rounded mb-3" />
					<div className="h-7 w-28 bg-pastel-light-gray rounded" />
				</div>
			))}
		</div>
	);
}

/** Compute average price change across tokens for trend indicator */
function computeAveragePriceChange(
	tokens: CreatorTokenWithStats[]
): { text: string; className: string } | null {
	let totalChange = 0;
	let count = 0;
	for (const token of tokens) {
		if (token.stats?.priceChange24h !== undefined && token.stats.priceChange24h !== null) {
			totalChange += token.stats.priceChange24h;
			count++;
		}
	}
	if (count === 0) return null;
	return formatPriceChange(totalChange / count);
}

/** Compute available (claimable) fees across all tokens */
function computeAvailableFees(tokens: CreatorTokenWithStats[]): string {
	let totalWei = 0n;
	for (const token of tokens) {
		if (token.accumulatedFees) {
			totalWei += BigInt(token.accumulatedFees.availableAmount);
		}
	}
	if (totalWei === 0n) return "";
	return `${formatNativeCurrency(totalWei, getNativeCurrencySymbol())} available to claim`;
}

export function DashboardSummary({ tokens, isLoading }: DashboardSummaryProps) {
	if (isLoading) {
		return <SummarySkeleton />;
	}

	const totalVolume = tokens.reduce((sum, t) => sum + (t.stats?.volume24h ?? 0), 0);
	const totalAccumulatedFees = computeTotalAccumulatedFees(tokens);
	const volumeTrend = computeAveragePriceChange(tokens);
	const availableFees = computeAvailableFees(tokens);

	return (
		<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
			<SummaryCard label="Tokens Created" value={formatCount(tokens.length)} />
			<SummaryCard
				label="Total 24h Volume"
				value={formatCompact(totalVolume)}
				trend={volumeTrend}
			/>
			<SummaryCard
				label="Your Total Accumulated Fees"
				value={totalAccumulatedFees}
				{...(availableFees ? { subtext: availableFees } : {})}
			/>
		</div>
	);
}
